import React, { useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import Layout from './components/Layout';
import { Property, SearchCriteria, BlogPost } from './types';
import { propertyAPI, blogAPI } from './services/api';

// --- Lazy Load Pages ---
const HomePage = React.lazy(() => import('./components/pages/HomePage'));
const PropertyGridPage = React.lazy(() => import('./components/pages/PropertyGridPage'));
const AboutPage = React.lazy(() => import('./components/pages/AboutPage'));
const ServicesPage = React.lazy(() => import('./components/pages/ServicesPage'));
const SearchResultsPage = React.lazy(() => import('./components/pages/SearchResultsPage'));
const ListPropertyPage = React.lazy(() => import('./components/pages/ListPropertyPage'));
const BlogPage = React.lazy(() => import('./components/pages/BlogPage'));
const BlogPostDetailPage = React.lazy(() => import('./components/pages/BlogPostDetailPage'));
const BuyersGuidePage = React.lazy(() => import('./components/pages/BuyersGuidePage'));
const SellersGuidePage = React.lazy(() => import('./components/pages/SellersGuidePage'));
const PrivacyPolicyPage = React.lazy(() => import('./components/pages/PrivacyPolicyPage'));
const MarketingPage = React.lazy(() => import('./components/pages/MarketingPage'));
const PropertyDetail = React.lazy(() => import('./components/PropertyDetail'));

// --- Loaders ---
export const appLoader = async () => {
  try {
    const [fetchedProperties, unverifiedProperties, fetchedBlogs] = await Promise.all([
      propertyAPI.getAll(),
      propertyAPI.getAll({ verified: false }),
      blogAPI.getAll()
    ]);

    const allProperties = [...fetchedProperties];
    unverifiedProperties.forEach((p) => {
      if (!allProperties.some((existing) => existing.id === p.id)) {
        allProperties.push(p);
      }
    });

    return { properties: allProperties, blogPosts: fetchedBlogs };
  } catch (error) {
    console.error('Error in appLoader:', error);
    return { properties: [], blogPosts: [] };
  }
};

// --- Page Wrappers to adapt Outlet Context to legacy props ---
const HomeWrapper = () => {
  const context = useOutletContext<any>() || { properties: [] };
  const properties = context.properties || [];

  const verifiedProperties = useMemo(() => properties.filter((p: any) => p.isVerified === true || p.verified === true), [properties]);
  const residentialProperties = useMemo(() => properties.filter((p: any) => p.category === 'Residential'), [properties]);
  const plotProperties = useMemo(() => properties.filter((p: any) => p.category === 'Plots'), [properties]);
  const commercialProperties = useMemo(() => properties.filter((p: any) => p.category === 'Commercial'), [properties]);

  const handlePropertySelect = (property: Property) => {
    context.navigate(`/property/${property.id}`);
  };

  const handleSearch = async (criteria: SearchCriteria) => {
    try {
      const results = await propertyAPI.search(criteria);
      context.setSearchResults(results);
    } catch (error) {
      context.setSearchResults([]);
    }
    const searchParams = new URLSearchParams();
    if (criteria.location) searchParams.set('location', criteria.location);
    if (criteria.category) searchParams.set('category', criteria.category);
    if (criteria.propertyType) searchParams.set('propertyType', criteria.propertyType);
    if (criteria.bedrooms) searchParams.set('bedrooms', criteria.bedrooms);
    context.navigate(`/search?${searchParams.toString()}`);
  };

  return (
    <HomePage
      latestProperties={properties}
      residentialProperties={residentialProperties}
      plotProperties={plotProperties}
      commercialProperties={commercialProperties}
      onPropertySelect={handlePropertySelect}
      onSearch={handleSearch}
      navigate={context.navigate}
    />
  );
};

const PropertyListWrapper = ({ filterBy, title, description }: any) => {
  const context = useOutletContext<any>() || { properties: [] };
  const properties = useMemo(() => (context.properties || []).filter((p: any) => p.category === filterBy), [context.properties, filterBy]);
  return (
    <PropertyGridPage
      title={title}
      description={description}
      properties={properties}
      onPropertySelect={(property: Property) => context.navigate(`/property/${property.id}`)}
    />
  );
};

const NavigateWrapper = ({ Component }: any) => {
  const context = useOutletContext<any>() || { navigate: () => { } };
  return <Component navigate={context.navigate} />;
};

const BlogWrapper = () => {
  const context = useOutletContext<any>() || { blogPosts: [] };
  const handleBlogPostSelect = (post: BlogPost) => context.navigate(`/blog/${post.id}`);
  return <BlogPage onBlogPostSelect={handleBlogPostSelect} blogPosts={context.blogPosts || []} />;
};

const BlogDetailWrapper = () => {
  const context = useOutletContext<any>() || { blogPosts: [] };
  const { id } = useParams();
  const post = (context.blogPosts || []).find((p: any) => String(p.id) === id);
  return post ? <BlogPostDetailPage post={post} onBack={() => window.history.back()} /> : <BlogPage onBlogPostSelect={(post: BlogPost) => context.navigate(`/blog/${post.id}`)} blogPosts={context.blogPosts || []} />;
};

const PropertyDetailWrapper = () => {
  const context = useOutletContext<any>() || { properties: [] };
  const { id } = useParams();
  const properties = context.properties || [];
  const verifiedProperties = useMemo(() => properties.filter((p: any) => p.isVerified === true || p.verified === true), [properties]);
  const property = properties.find((p: any) => String(p.id) === id);

  return property ? (
    <PropertyDetail
      property={property}
      latestProperties={verifiedProperties}
      onPropertySelect={(prop: Property) => context.navigate(`/property/${prop.id}`)}
      onBack={() => window.history.back()}
    />
  ) : (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Property Not Found</h2>
      <p className="text-gray-600 mb-6">The property you are looking for does not exist or has been removed.</p>
      <button
        onClick={() => context.navigate('/')}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Go Home
      </button>
    </div>
  );
};

const SearchResultsWrapper = () => {
  const context = useOutletContext<any>() || { properties: [], searchResults: [] };
  const properties = context.properties || [];
  const verifiedProperties = useMemo(() => properties.filter((p: any) => p.isVerified === true || p.verified === true), [properties]);

  return (
    <SearchResultsPage
      properties={context.searchResults || []}
      recommendedProperties={verifiedProperties}
      onPropertySelect={(prop: Property) => context.navigate(`/property/${prop.id}`)}
    />
  );
};

// --- Route Definitions ---
export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: Layout,
    loader: appLoader,
    children: [
      { index: true, Component: HomeWrapper },
      { path: 'residential', element: <PropertyListWrapper filterBy="Residential" title="Residential Properties" description="Find your dream home from our collection of verified apartments and villas." /> },
      { path: 'plots', element: <PropertyListWrapper filterBy="Plots" title="Plots for Sale" description="Secure the perfect piece of land for your dream home or investment." /> },
      { path: 'commercial', element: <PropertyListWrapper filterBy="Commercial" title="Commercial Properties" description="Find the ideal space for your business, from office spaces to retail shops." /> },
      { path: 'about', element: <NavigateWrapper Component={AboutPage} /> },
      { path: 'services', element: <NavigateWrapper Component={ServicesPage} /> },
      { path: 'blog', Component: BlogWrapper },
      {
        path: 'blog/:id',
        Component: BlogDetailWrapper,
        getStaticPaths: async () => {
          const blogs = await blogAPI.getAll();
          return (blogs || []).map((b) => `/blog/${b.id}`);
        },
      },
      {
        path: 'property/:id',
        Component: PropertyDetailWrapper,
        getStaticPaths: async () => {
          const props = await propertyAPI.getAll();
          return (props || []).map((p) => `/property/${p.id}`);
        },
      },
      { path: 'buyers-guide', Component: BuyersGuidePage },
      { path: 'sellers-guide', Component: SellersGuidePage },
      { path: 'privacy-policy', Component: PrivacyPolicyPage },
      { path: 'list-property', Component: ListPropertyPage },
      { path: 'partner', element: <NavigateWrapper Component={MarketingPage} /> },
      { path: 'search', Component: SearchResultsWrapper },
    ],
  },
];
